import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePermissions } from '@/composables/usePermissions'
import { rememberRedirect } from '@/utils/redirectAfterLogin'

const routes = [
  {
    // Racine publique : page de présentation pour les visiteurs, tableau de
    // bord pour les utilisateurs connectés. C'est RootView qui arbitre, ce
    // qui évite de déplacer l'application sur une autre adresse.
    path: '/',
    name: 'Home',
    component: () => import('@/views/RootView.vue'),
    meta: {
      requiresAuth: false,
      permissions: [],
      requiresAny: false
    }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/Profile.vue'),
    meta: {
      requiresAuth: true,
      permissions: ['read_own_user'],
      requiresAny: false
    }
  },
  {
    path: '/access-denied',
    name: 'AccessDenied',
    component: () => import('@/views/AccessDenied.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/user-management',
    name: 'UserManagement',
    component: () => import('@/views/UserManagement.vue'),
    meta: {
      requiresAuth: true,
      permissions: ['admin_access', 'read_users'],
      requiresAny: false
    }
  },
  {
    path: '/establishments',
    name: 'EstablishmentManagement',
    component: () => import('@/views/EstablishmentManagement.vue'),
    meta: {
      requiresAuth: true,
      // `permissions` ne conviendrait pas : hasAnyPermission/hasAllPermissions
      // court-circuitent à true pour tout rôle admin, qui obtiendrait donc
      // l'accès alors que l'API refuse `write_establishments`.
      superadminOnly: true
    }
  },
  {
    path: '/patient-care',
    name: 'PatientCare',
    component: () => import('@/views/PatientCare.vue'),
    meta: {
      requiresAuth: true,
      permissions: ['read_patients', 'read_patient_cares', 'read_own_patient_cares'],
      requiresAny: true
    }
  },
  {
    path: '/leave-request',
    name: 'LeaveRequest',
    component: () => import('@/views/LeaveRequest.vue'),
    meta: {
      requiresAuth: true,
      permissions: ['read_own_leave_requests', 'read_leave_requests'],
      requiresAny: true
    }
  },
  {
    path: '/schedule',
    name: 'Schedule',
    component: () => import('@/views/Calendar.vue'),
    meta: {
      requiresAuth: true,
      permissions: ['read_own_schedule', 'read_schedules'],
      requiresAny: true
    }
  },
  {
    path: '/calendar',
    name: 'Calendar',
    component: () => import('@/views/Calendar.vue'),
    meta: {
      requiresAuth: true,
      permissions: ['read_own_schedule', 'read_schedules'],
      requiresAny: true
    }
  },
  {
    path: '/stock',
    name: 'Stock',
    component: () => import('@/views/Stock.vue'),
    meta: {
      requiresAuth: true,
      permissions: ['write_products', 'delete_products'],
      requiresAny: true
    }
  },
  {
    path: '/pointage',
    name: 'Pointage',
    component: () => import('@/views/Pointage.vue'),
    meta: {
      requiresAuth: true,
      permissions: [],
      requiresAny: false
    }
  },
  {
    path: '/lab-order',
    name: 'LabOrder',
    component: () => import('@/views/LabOrder.vue'),
    meta: {
      requiresAuth: true,
      permissions: ['view_lab_page', 'create_lab_form'],
      requiresAny: true
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/select-establishment',
    name: 'SelectEstablishment',
    component: () => import('@/views/EstablishmentSelectView.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: {
      requiresAuth: false
    }
  },
  {
    path: '/payslips',
    name: 'Payslip',
    component: () => import('@/views/Payslip.vue'),
    meta: {
      // Le garde ne lit que `permissions`/`requiresAny` : les clés
      // `requiredPermissions`/`permissionOperator` étaient ignorées,
      // la route n'était donc pas protégée.
      requiresAuth: true,
      permissions: ['read_payslips', 'read_own_payslips'],
      requiresAny: true
    }
  },
  {
    path: '/desmos',
    name: 'Desmos',
    component: () => import('@/views/Desmos.vue'),
    meta: {
      // Recherche patient et création de PEC : réservé aux profils qui
      // gèrent les prises en charge (les dentistes n'y ont pas accès).
      requiresAuth: true,
      permissions: ['write_patient_cares'],
      requiresAny: false
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  if (to.name === 'Login') {
    if (authStore.isAuthenticated && authStore.currentUser) {
      next('/')
      return
    } else {
      next()
      return
    }
  }

  // La racine est publique, mais un utilisateur connecté y retrouve son
  // tableau de bord : il doit donc passer par les mêmes préalables que sur
  // n'importe quelle page de l'application (session hydratée, centre choisi).
  if (to.name === 'Home' && authStore.isAuthenticated) {
    if (!authStore.currentUser) {
      try {
        await authStore.fetchCurrentUser()
      } catch (error) {
        authStore.logout()
        next()
        return
      }
    }
    if (authStore.needsEstablishmentSelection) {
      next('/select-establishment')
      return
    }
  }

  if (!to.meta.requiresAuth) {
    next()
    return
  }

  if (!authStore.isAuthenticated) {
    // Sans cette mémorisation, un lien reçu par email (les notifications de
    // fiche de paie pointent vers /payslips) renverrait sur l'accueil.
    rememberRedirect(to.fullPath)
    next('/login')
    return
  }

  if (authStore.isAuthenticated && !authStore.currentUser) {
    try {
      await authStore.fetchCurrentUser()
    } catch (error) {
      // Session expirée en cours de route : on revient là où l'utilisateur
      // allait, pas à l'accueil.
      authStore.logout()
      rememberRedirect(to.fullPath)
      next('/login')
      return
    }
  }

  if (to.name !== 'SelectEstablishment' && authStore.needsEstablishmentSelection) {
    next('/select-establishment')
    return
  }

  const { hasAnyPermission, hasAllPermissions, isSuperAdmin } = usePermissions()

  // Contrôle du rôle, et non d'une permission : voir le commentaire de la route
  // /establishments.
  if (to.meta.superadminOnly && !isSuperAdmin.value) {
    next(to.name === 'AccessDenied' ? '/login' : '/access-denied')
    return
  }

  if (!to.meta.permissions || to.meta.permissions.length === 0) {
    next()
    return
  }

  let hasAccess

  if (to.meta.requiresAny) {
    hasAccess = hasAnyPermission(to.meta.permissions)
  } else {
    hasAccess = hasAllPermissions(to.meta.permissions)
  }

  // Si l'utilisateur n'a pas les permissions
  if (!hasAccess) {
    if (to.name !== 'AccessDenied') {
      next('/access-denied')
    } else {
      // Éviter la boucle infinie
      next('/login')
    }
    return
  }

  next()
})

export default router
